import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  BarChart2,
  Check,
  Code,
  Copy,
  Edit2,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  Layers,
  Layout,
  Maximize2,
  Megaphone,
  MousePointerClick,
  Plus,
  Power,
  Trash2,
  X,
  HelpCircle,
  Smartphone,
  Monitor,
  Tablet,
  TrendingUp,
  Award,
  Video
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { motion } from "motion/react";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import {
  AdLayoutBadge,
  AdLayoutGuideModal,
  AdLayoutSchematic,
  AdLayoutPreviewModal,
  AdActualRender
} from "../../components/AdLayoutVisualizer";
import { useApi } from "../../hooks/useApi";
import { apiSend, apiUpload } from "../../services/api";
import type { Advertisement } from "../../types";
import { AD_LAYOUT_SPECS, getAdLayoutSpec } from "../../utils/adLayoutSpecs";

const PLACEMENTS = [
  {
    value: "homepage_top",
    label: "Homepage Top Billboard",
    dimensions: "970 × 250 px",
    type: "Billboard Banner"
  },
  {
    value: "homepage_hero",
    label: "Homepage Hero Lead Slot",
    dimensions: "1200 × 380 px",
    type: "Hero Feature Display"
  },
  {
    value: "homepage_inline",
    label: "Homepage Inline Break",
    dimensions: "970 × 150 px",
    type: "In-Feed Interstitial Banner"
  },
  {
    value: "homepage_sponsored",
    label: "Homepage Sponsored Spotlight Feed",
    dimensions: "380 × 280 px",
    type: "Native Card Grid"
  },
  {
    value: "article_top",
    label: "Article Top Header",
    dimensions: "728 × 90 px",
    type: "Masthead Leaderboard"
  },
  {
    value: "article_middle",
    label: "Article Mid-Prose Break",
    dimensions: "728 × 90 px / 300 × 250 px",
    type: "In-Article MPU Break"
  },
  {
    value: "article_bottom",
    label: "Article Footer End",
    dimensions: "970 × 200 px",
    type: "Outro Strip Banner"
  },
  {
    value: "sidebar",
    label: "Sidebar Rail",
    dimensions: "300 × 250 px / 300 × 600 px",
    type: "Sidebar MPU / Skyscraper"
  },
  {
    value: "category_page",
    label: "Category Hub Banner",
    dimensions: "970 × 200 px",
    type: "Category Section Leaderboard"
  },
  {
    value: "newsletter",
    label: "Newsletter Email Dispatch",
    dimensions: "600 × 200 px",
    type: "Email Sponsor Banner"
  }
];

type AdFormData = {
  id?: number;
  name: string;
  ad_type: "image" | "text" | "html" | "video";
  placement: string;
  destination_url: string;
  image_url: string;
  video_url: string;
  alt_text: string;
  sponsor_name: string;
  headline: string;
  body_text: string;
  cta_text: string;
  html_content: string;
  priority: number;
  active: boolean;
};

const initialFormData: AdFormData = {
  name: "",
  ad_type: "text",
  placement: "homepage_top",
  destination_url: "https://",
  image_url: "",
  video_url: "",
  alt_text: "",
  sponsor_name: "",
  headline: "",
  body_text: "",
  cta_text: "Learn More",
  html_content: "",
  priority: 5,
  active: true
};

export function AdvertisingPage() {
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const { data: ads, loading, error } = useApi<Advertisement[]>(
    "/api/advertisements",
    [reloadTrigger]
  );

  const [filterType, setFilterType] = useState<string>("all");
  const [filterPlacement, setFilterPlacement] = useState<string>("all");
  
  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [selectedGuidePlacement, setSelectedGuidePlacement] = useState<string>("homepage_top");
  const [editingAd, setEditingAd] = useState<AdFormData | null>(null);
  
  // Layout Preview Modal State
  const [previewModalAd, setPreviewModalAd] = useState<Advertisement | null>(null);
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "tablet" | "mobile">("desktop");
  
  // Form Live Preview State
  const [formPreviewViewport, setFormPreviewViewport] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [formShowSampleContent, setFormShowSampleContent] = useState<boolean>(true);
  
  // Embed Snippet Modal State
  const [snippetModalAd, setSnippetModalAd] = useState<Advertisement | null>(null);
  const [copiedSnippetId, setCopiedSnippetId] = useState<number | null>(null);

  // Performance Chart metric mode
  const [chartMetric, setChartMetric] = useState<"ctr" | "impressions_clicks">("ctr");

  const [saving, setSaving] = useState(false);
  const [uploadingCreative, setUploadingCreative] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const filteredAds = useMemo(() => {
    if (!ads) return [];
    return ads.filter((ad) => {
      if (filterType !== "all" && (ad.ad_type || "image") !== filterType) return false;
      if (filterPlacement !== "all" && ad.placement !== filterPlacement) return false;
      return true;
    });
  }, [ads, filterType, filterPlacement]);

  // Aggregate Metrics
  const metrics = useMemo(() => {
    if (!ads) return { total: 0, active: 0, impressions: 0, clicks: 0, ctr: "0.00" };
    const total = ads.length;
    const active = ads.filter((a) => a.active !== false).length;
    const impressions = ads.reduce((acc, a) => acc + (Number(a.impressions) || 0), 0);
    const clicks = ads.reduce((acc, a) => acc + (Number(a.clicks) || 0), 0);
    const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : "0.00";
    return { total, active, impressions, clicks, ctr };
  }, [ads]);

  // Chart Data for Performance comparison
  const performanceChartData = useMemo(() => {
    if (!ads) return [];
    return ads
      .filter((a) => a.active !== false || Number(a.impressions) > 0)
      .map((a) => {
        const imps = Number(a.impressions) || 0;
        const clks = Number(a.clicks) || 0;
        const ctr = imps > 0 ? Number(((clks / imps) * 100).toFixed(2)) : 0;
        const spec = getAdLayoutSpec(a.placement);
        return {
          id: a.id,
          name: a.name.length > 20 ? a.name.substring(0, 18) + "..." : a.name,
          fullName: a.name,
          sponsor: a.sponsor_name || "Direct Commercial",
          placement: spec.shortLabel,
          dimensions: spec.dimensions.desktop,
          impressions: imps,
          clicks: clks,
          ctr: ctr,
          active: a.active !== false
        };
      })
      .sort((a, b) => b.ctr - a.ctr);
  }, [ads]);

  const handleOpenCreate = () => {
    setEditingAd({ ...initialFormData });
    setActionError("");
    setIsModalOpen(true);
  };

  const handleOpenEdit = (ad: Advertisement) => {
    setEditingAd({
      id: ad.id,
      name: ad.name,
      ad_type: ad.ad_type || (ad.html_content ? "html" : ad.headline ? "text" : "image"),
      placement: ad.placement,
      destination_url: ad.destination_url,
      image_url: ad.image_url || "",
      video_url: ad.video_url || "",
      alt_text: ad.alt_text || "",
      sponsor_name: ad.sponsor_name || "",
      headline: ad.headline || "",
      body_text: ad.body_text || "",
      cta_text: ad.cta_text || "Learn More",
      html_content: ad.html_content || "",
      priority: ad.priority ?? 5,
      active: ad.active !== false
    });
    setActionError("");
    setIsModalOpen(true);
  };

  const handleToggleActive = async (ad: Advertisement) => {
    try {
      const nextActive = ad.active === false;
      await apiSend(`/api/advertisements/${ad.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: nextActive })
      });
      setReloadTrigger((n) => n + 1);
    } catch (err: any) {
      alert(err.message || "Failed to update ad status");
    }
  };

  const handleDelete = async (ad: Advertisement) => {
    if (!window.confirm(`Are you sure you want to delete campaign "${ad.name}"?`)) return;
    try {
      await apiSend(`/api/advertisements/${ad.id}`, {
        method: "DELETE"
      });
      setReloadTrigger((n) => n + 1);
    } catch (err: any) {
      alert(err.message || "Failed to delete ad");
    }
  };

  const handleCopySnippet = (ad: Advertisement) => {
    const spec = getAdLayoutSpec(ad.placement);
    const snippet = `<!-- ZimSmart Ad Slot: ${ad.name} (${spec.shortLabel} - ${spec.dimensions.desktop}) -->
<div class="zimsmart-ad-slot" data-ad-id="${ad.id}" data-placement="${ad.placement}" data-ad-type="${ad.ad_type || 'image'}">
  <a href="${ad.destination_url}" target="_blank" rel="noopener sponsored" style="display:block;text-decoration:none;">
    ${
      ad.image_url
        ? `<img src="${ad.image_url}" alt="${ad.alt_text || ad.name}" style="max-width:100%;height:auto;border-radius:4px;" />`
        : `<div style="padding:16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;">
      <small style="color:#b45309;font-weight:700;text-transform:uppercase;">Sponsored by ${ad.sponsor_name || 'Partner'}</small>
      <h4 style="margin:4px 0 8px;font-weight:700;color:#0f172a;">${ad.headline || ad.name}</h4>
      <p style="margin:0 0 10px;font-size:13px;color:#475569;">${ad.body_text || ''}</p>
      <span style="display:inline-block;padding:6px 12px;background:#b45309;color:#fff;font-size:12px;font-weight:700;border-radius:4px;">${ad.cta_text || 'Learn More'}</span>
    </div>`
    }
  </a>
</div>`;

    navigator.clipboard.writeText(snippet);
    setCopiedSnippetId(ad.id);
    setTimeout(() => {
      setCopiedSnippetId(null);
    }, 2000);
  };

  const uploadCreative = async (file: File) => {
    if (!editingAd) return;
    const gif = editingAd.ad_type === "image";
    const accepted = gif ? ["image/gif"] : ["video/mp4", "video/webm"];
    if (!accepted.includes(file.type)) { setActionError(gif ? "Choose an animated GIF file" : "Choose an MP4 or WebM video"); return; }
    if (file.size > 50 * 1024 * 1024) { setActionError("File exceeds the 50 MB upload limit"); return; }
    setUploadingCreative(true);
    setActionError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const uploaded = await apiUpload<{ url: string }>("/api/advertisements/upload", form);
      setEditingAd((current) => current ? { ...current, [gif ? "image_url" : "video_url"]: uploaded.url } : current);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Upload failed");
    } finally { setUploadingCreative(false); }
  };

  const handleSaveAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAd || uploadingCreative) return;

    if (!editingAd.name.trim()) {
      setActionError("Campaign name is required");
      return;
    }
    if (!editingAd.destination_url.trim()) {
      setActionError("Destination URL is required");
      return;
    }

    setSaving(true);
    setActionError("");

    try {
      const payload = {
        name: editingAd.name.trim(),
        ad_type: editingAd.ad_type,
        video_url: editingAd.ad_type === "video" ? editingAd.video_url.trim() || null : null,
        image_url: editingAd.image_url.trim() || null,
        placement: editingAd.placement,
        destination_url: editingAd.destination_url.trim(),
        sponsor_name: editingAd.sponsor_name.trim() || null,
        headline: editingAd.headline.trim() || null,
        body_text: editingAd.body_text.trim() || null,
        cta_text: editingAd.cta_text.trim() || null,
        html_content: editingAd.html_content.trim() || null,
        alt_text: editingAd.alt_text.trim() || null,
        priority: Number(editingAd.priority) || 0,
        active: editingAd.active
      };

      if (editingAd.id) {
        await apiSend(`/api/advertisements/${editingAd.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload)
        });
        setActionSuccess("Advertisement campaign updated successfully!");
      } else {
        await apiSend("/api/advertisements", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        setActionSuccess("New advertisement campaign launched successfully!");
      }

      setTimeout(() => {
        setIsModalOpen(false);
        setActionSuccess("");
        setReloadTrigger((n) => n + 1);
      }, 700);
    } catch (err: any) {
      setActionError(err.message || "Failed to save advertisement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-page">
      <header className="admin-page-header flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1>Commercial Advertising & Sponsored Campaigns</h1>
            <span className="text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-2.5 py-0.5 rounded-full border border-amber-300">
              Layout & Sizing Matrix Active
            </span>
          </div>
          <p className="text-slate-600 mt-1">
            Deploy high-impact display banners, native editorial text posts, and custom HTML/interactive ads with precise IAB dimensional specs.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => {
              setSelectedGuidePlacement("homepage_top");
              setIsGuideModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-colors shadow-2xs"
          >
            <Layout size={15} className="text-amber-700" />
            <span>Ad Layout & Size Matrix</span>
          </button>

          <button
            type="button"
            onClick={handleOpenCreate}
            className="mag-admin-primary-btn"
          >
            <Plus size={16} />
            <span>Launch New Ad / Post</span>
          </button>
        </div>
      </header>

      {/* KPI Stats Grid */}
      <div className="mag-admin-metric-grid">
        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Active Campaigns</span>
            <Megaphone size={16} className="text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {metrics.active}{" "}
            <span className="text-sm font-normal text-slate-500">/ {metrics.total} Total</span>
          </div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Total Impressions</span>
            <Eye size={16} className="text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {metrics.impressions.toLocaleString()}
          </div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Tracked Clicks</span>
            <MousePointerClick size={16} className="text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {metrics.clicks.toLocaleString()}
          </div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Average CTR</span>
            <BarChart2 size={16} className="text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{metrics.ctr}%</div>
        </div>
      </div>

      {/* AD PERFORMANCE SUMMARY RECHARTS CARD */}
      <motion.div
        className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <BarChart2 size={18} className="text-amber-600" />
              <h2 className="text-base font-serif font-bold text-slate-900">
                Ad Unit Performance & Click-Through-Rate (CTR)
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Comparative benchmark across active advertising placements and formats.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Metric View:</span>
            <div className="flex items-center bg-slate-100 p-1 rounded-md text-xs font-semibold">
              <button
                type="button"
                onClick={() => setChartMetric("ctr")}
                className={`px-3 py-1 rounded transition-all ${
                  chartMetric === "ctr"
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                CTR Comparison (%)
              </button>
              <button
                type="button"
                onClick={() => setChartMetric("impressions_clicks")}
                className={`px-3 py-1 rounded transition-all ${
                  chartMetric === "impressions_clicks"
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Impressions & Clicks
              </button>
            </div>
          </div>
        </div>

        {performanceChartData.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            No active advertising campaign performance data recorded yet.
          </div>
        ) : (
          <div className="mt-4">
            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                {chartMetric === "ctr" ? (
                  <BarChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 15, left: -20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      angle={-20}
                      textAnchor="end"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                      unit="%"
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          return (
                            <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg text-xs space-y-1">
                              <div className="font-bold text-amber-300">{d.fullName}</div>
                              <div className="text-slate-300 font-medium">Sponsor: {d.sponsor}</div>
                              <div className="text-slate-300">Layout: {d.placement} ({d.dimensions})</div>
                              <div className="pt-1 border-t border-slate-800 flex justify-between gap-4 font-mono">
                                <span>Impressions: {d.impressions.toLocaleString()}</span>
                                <span>Clicks: {d.clicks.toLocaleString()}</span>
                              </div>
                              <div className="text-amber-400 font-bold font-mono text-sm pt-0.5">
                                CTR: {d.ctr}%
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="ctr" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                      {performanceChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === 0 ? "#10b981" : index % 2 === 0 ? "#d97706" : "#f59e0b"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <BarChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 15, left: -10, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      angle={-20}
                      textAnchor="end"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          return (
                            <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg text-xs space-y-1">
                              <div className="font-bold text-slate-100">{d.fullName}</div>
                              <div className="text-slate-300 font-medium">Layout: {d.placement}</div>
                              <div className="pt-1 border-t border-slate-800 space-y-0.5 font-mono">
                                <div className="text-blue-300">Impressions: {d.impressions.toLocaleString()}</div>
                                <div className="text-emerald-400">Clicks: {d.clicks.toLocaleString()}</div>
                                <div className="text-amber-400 font-bold">CTR: {d.ctr}%</div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="impressions" fill="#3b82f6" name="Impressions" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="clicks" fill="#10b981" name="Clicks" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
            
            <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span>Top Performing Unit</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span>Active Ad Campaign</span>
                </span>
              </div>
              <div className="font-mono text-slate-600">
                Industry Benchmark CTR: <strong className="text-slate-900">0.8% - 2.5%</strong>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Filter Toolbar */}
      <div className="mag-admin-filters-bar flex flex-wrap items-center justify-between gap-4 bg-white p-4 border border-slate-200 rounded-lg mb-6">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Format:</span>
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-md">
            {[
              { id: "all", label: "All Formats" },
              { id: "text", label: "Text / Sponsored" },
              { id: "html", label: "HTML Posts" },
              { id: "image", label: "Image Banners" }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFilterType(tab.id)}
                className={`px-3 py-1 text-xs font-semibold rounded ${
                  filterType === tab.id
                    ? "bg-white text-slate-900 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="filter-placement" className="text-xs font-bold text-slate-600 uppercase">
            Ad Layout / Placement:
          </label>
          <select
            id="filter-placement"
            value={filterPlacement}
            onChange={(e) => setFilterPlacement(e.target.value)}
            className="text-xs border border-slate-300 rounded px-2.5 py-1.5 bg-white text-slate-800 font-medium"
          >
            <option value="all">All Ad Layouts & Slots</option>
            {PLACEMENTS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label} ({p.dimensions})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Campaigns Listing Table */}
      {loading ? (
        <LoadingState label="Loading commercial campaigns..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : !filteredAds.length ? (
        <EmptyState
          title="No campaigns found"
          body="Click 'Launch New Ad / Post' above to create native sponsored posts, HTML ads, or display banners."
        />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-600 uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">Campaign & Sponsor</th>
                <th className="py-3 px-4">Format</th>
                <th className="py-3 px-4">Ad Layout & Dimensions</th>
                <th className="py-3 px-4 text-center">Priority</th>
                <th className="py-3 px-4 text-right">Impressions</th>
                <th className="py-3 px-4 text-right">Clicks</th>
                <th className="py-3 px-4 text-right">CTR</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAds.map((ad) => {
                const imps = Number(ad.impressions) || 0;
                const clks = Number(ad.clicks) || 0;
                const ctr = imps > 0 ? ((clks / imps) * 100).toFixed(1) + "%" : "0.0%";
                const adType = ad.ad_type || (ad.html_content ? "html" : ad.headline ? "text" : "image");
                const spec = getAdLayoutSpec(ad.placement);
                const isCopied = copiedSnippetId === ad.id;

                return (
                  <tr key={ad.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-slate-900">{ad.name}</div>
                      {ad.sponsor_name && (
                        <div className="text-xs text-amber-700 font-medium mt-0.5">
                          Sponsor: {ad.sponsor_name}
                        </div>
                      )}
                      {ad.headline && (
                        <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                          "{ad.headline}"
                        </div>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      {adType === "html" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-purple-100 text-purple-800 rounded">
                          <Code size={11} /> HTML Post
                        </span>
                      ) : adType === "text" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
                          <FileText size={11} /> Text / Sponsored
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                          <ImageIcon size={11} /> Image Banner
                        </span>
                      )}
                    </td>

                    {/* Rich Ad Layout Type & Dimensions Column */}
                    <td className="py-3.5 px-4">
                      <div
                        className="cursor-pointer group inline-block"
                        onClick={() => {
                          setPreviewModalAd(ad);
                        }}
                        title="Click to open wireframe and layout preview"
                      >
                        <AdLayoutBadge
                          placement={ad.placement}
                          adType={adType as "image" | "text" | "html"}
                          size="md"
                        />
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-center font-mono text-xs font-semibold text-slate-700">
                      {ad.priority || 0}
                    </td>

                    <td className="py-3.5 px-4 text-right font-mono text-slate-700">
                      {imps.toLocaleString()}
                    </td>

                    <td className="py-3.5 px-4 text-right font-mono text-slate-700">
                      {clks.toLocaleString()}
                    </td>

                    <td className="py-3.5 px-4 text-right font-mono font-semibold text-slate-900">
                      {ctr}
                    </td>

                    {/* Simple Status Toggle (Active / Paused) */}
                    <td className="py-3.5 px-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(ad)}
                        className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full transition-all shadow-2xs ${
                          ad.active !== false
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"
                            : "bg-slate-200 text-slate-600 border border-slate-300 hover:bg-slate-300"
                        }`}
                        title={ad.active !== false ? "Click to Pause Campaign" : "Click to Activate Campaign"}
                      >
                        <Power size={11} />
                        <span>{ad.active !== false ? "Active" : "Paused"}</span>
                      </button>
                    </td>

                    {/* Actions: Copy Snippet, Layout Preview, Edit, External, Delete */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {/* Copy Ad Snippet Button */}
                        <button
                          type="button"
                          onClick={() => handleCopySnippet(ad)}
                          className={`p-1.5 rounded transition-all ${
                            isCopied
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                          }`}
                          title={isCopied ? "Copied Snippet!" : "Copy Ad Embed Snippet"}
                        >
                          {isCopied ? <Check size={15} className="text-emerald-700" /> : <Copy size={15} />}
                        </button>

                        {/* Layout Preview Wireframe Button */}
                        <button
                          type="button"
                          onClick={() => setPreviewModalAd(ad)}
                          className="p-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded"
                          title="Open Wireframe Layout Preview"
                        >
                          <Maximize2 size={15} />
                        </button>

                        {/* Edit Button */}
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(ad)}
                          className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="Edit Campaign & Layout"
                        >
                          <Edit2 size={15} />
                        </button>

                        {/* Destination Link */}
                        <a
                          href={ad.destination_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-slate-500 hover:text-amber-700 hover:bg-slate-100 rounded"
                          title="Visit Destination"
                        >
                          <ExternalLink size={15} />
                        </a>

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={() => handleDelete(ad)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Delete Campaign"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* LIVE LAYOUT & ACTUAL AD PREVIEW MODAL */}
      {previewModalAd && (
        <AdLayoutPreviewModal
          ad={previewModalAd}
          onClose={() => setPreviewModalAd(null)}
          onEdit={(ad) => handleOpenEdit(ad)}
          onCopySnippet={(ad) => handleCopySnippet(ad)}
        />
      )}

      {/* CREATE / EDIT MODAL WITH LIVE AD LAYOUT SCHEMATIC & DIMENSION PREVIEW */}
      {isModalOpen && editingAd && (
        <div className="mag-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div
            className="mag-modal-card max-w-3xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mag-modal-close-btn"
            >
              <X size={18} />
            </button>

            <div className="p-6">
              <div className="pb-3 mb-4 border-b border-slate-200">
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  {editingAd.id ? `Edit Campaign: ${editingAd.name}` : "Launch New Commercial Ad / Sponsored Post"}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Configure format, copy, visual creatives, and layout positioning for desktop & mobile.
                </p>
              </div>

              {actionError && <div className="mag-modal-error mb-4">{actionError}</div>}
              {actionSuccess && (
                <div className="p-3 bg-emerald-50 text-emerald-800 text-sm font-semibold rounded mb-4 flex items-center gap-2">
                  <Check size={16} /> {actionSuccess}
                </div>
              )}

              <form onSubmit={handleSaveAd} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Campaign Internal Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editingAd.name}
                      onChange={(e) => setEditingAd({ ...editingAd, name: e.target.value })}
                      placeholder="e.g. Econet 5G Launch - Billboard"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Destination Click URL *
                    </label>
                    <input
                      type="url"
                      required
                      value={editingAd.destination_url}
                      onChange={(e) => setEditingAd({ ...editingAd, destination_url: e.target.value })}
                      placeholder="https://example.com/campaign"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-600"
                    />
                  </div>
                </div>

                {/* Ad Format Selector */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                    Ad Unit Format
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                    {[
                      { id: "text", label: "Native Text / Sponsored", icon: FileText, desc: "Sponsored story with sponsor label & CTA" },
                      { id: "image", label: "Display Banner", icon: ImageIcon, desc: "Static or animated creative (JPG/PNG/WebP/GIF)" },
                      { id: "html", label: "Custom HTML Embed", icon: Code, desc: "Interactive widgets & rich media scripts" },
                      { id: "video", label: "Video Ad", icon: Video, desc: "MP4/WebM via HTTPS URL" }
                    ].map((fmt) => {
                      const Icon = fmt.icon;
                      const isSelected = editingAd.ad_type === fmt.id;
                      return (
                        <button
                          key={fmt.id}
                          type="button"
                          onClick={() => setEditingAd({ ...editingAd, ad_type: fmt.id as any })}
                          className={`p-3 text-left rounded-lg border transition-all ${
                            isSelected
                              ? "bg-amber-50 border-amber-400 text-slate-900 shadow-2xs"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center gap-2 font-bold text-xs">
                            <Icon size={15} className={isSelected ? "text-amber-700" : "text-slate-400"} />
                            <span>{fmt.label}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">{fmt.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>


                {(editingAd.ad_type === "video" || editingAd.ad_type === "image") && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                    <label className="block text-xs font-bold text-slate-800 mb-2">Upload {editingAd.ad_type === "image" ? "Animated GIF" : "MP4 / WebM Video"} (up to 50 MB)</label>
                    <input type="file" accept={editingAd.ad_type === "image" ? "image/gif,.gif" : "video/mp4,video/webm,.mp4,.webm"} disabled={uploadingCreative} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadCreative(file); e.target.value = ""; }} className="w-full text-sm" />
                    {uploadingCreative && <p role="status" className="text-xs mt-2">Uploading creative…</p>}
                    {editingAd.ad_type === "image" && editingAd.image_url && <img src={editingAd.image_url} alt="GIF advertisement preview" className="mt-3 w-full max-h-44 object-contain" />}
                  </div>
                )}
                {editingAd.ad_type === "video" && (
                  <div className="space-y-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <label className="block text-xs font-bold uppercase text-slate-700" htmlFor="zimsmart-video-ad-url">Video file URL (HTTPS or uploaded MP4/WebM) *</label>
                    <input id="zimsmart-video-ad-url" type="url" required value={editingAd.video_url} onChange={(e) => setEditingAd({ ...editingAd, video_url: e.target.value })} placeholder="https://cdn.example.com/campaign.mp4" className="w-full border rounded px-3 py-2 text-sm" />
                    <p className="text-xs text-slate-600">Video plays muted and inline. The destination URL above is the sponsored click-through. Use a file URL, not a YouTube watch-page URL.</p>
                    {editingAd.video_url && <video src={editingAd.video_url} controls muted playsInline preload="metadata" className="w-full max-h-64 rounded bg-black" />}
                  </div>
                )}
                {/* Layout / Placement Selector */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-slate-700 uppercase">
                      Ad Layout & Placement Slot *
                    </label>
                    <span className="text-[11px] text-amber-700 font-semibold font-mono">
                      Target: {getAdLayoutSpec(editingAd.placement).dimensions.desktop}
                    </span>
                  </div>
                  <select
                    value={editingAd.placement}
                    onChange={(e) => setEditingAd({ ...editingAd, placement: e.target.value })}
                    className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-medium bg-white focus:outline-none focus:border-slate-600"
                  >
                    {PLACEMENTS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label} — {p.type} ({p.dimensions})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Specific Fields for Text / Native Sponsored Posts */}
                {editingAd.ad_type === "text" && (
                  <div className="space-y-3 p-3.5 bg-amber-50/40 border border-amber-200/60 rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Sponsor / Brand Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Stanbic Bank Zimbabwe"
                          value={editingAd.sponsor_name}
                          onChange={(e) => setEditingAd({ ...editingAd, sponsor_name: e.target.value })}
                          className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Call-to-Action (CTA) Button
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Open Account Today"
                          value={editingAd.cta_text}
                          onChange={(e) => setEditingAd({ ...editingAd, cta_text: e.target.value })}
                          className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white font-semibold"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Native Headline Title
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Multi-Currency Corporate Forex Routing Across Africa"
                        value={editingAd.headline}
                        onChange={(e) => setEditingAd({ ...editingAd, headline: e.target.value })}
                        className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white font-medium"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Body Description Text
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Describe the product, service, or editorial insight in 1-2 concise sentences..."
                        value={editingAd.body_text}
                        onChange={(e) => setEditingAd({ ...editingAd, body_text: e.target.value })}
                        className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Specific Fields for Custom HTML Ads */}
                {editingAd.ad_type === "html" && (
                  <div className="space-y-3 p-3.5 bg-purple-50/40 border border-purple-200/60 rounded-lg">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Sponsor / Creator Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Econet Wireless Enterprise"
                        value={editingAd.sponsor_name}
                        onChange={(e) => setEditingAd({ ...editingAd, sponsor_name: e.target.value })}
                        className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Custom HTML / Embed Snippet
                      </label>
                      <textarea
                        rows={4}
                        placeholder="<div class='custom-banner'><h4>Special Offer</h4><p>Click below to claim...</p></div>"
                        value={editingAd.html_content}
                        onChange={(e) => setEditingAd({ ...editingAd, html_content: e.target.value })}
                        className="w-full text-xs font-mono border border-slate-300 rounded px-3 py-2 bg-slate-900 text-emerald-400"
                      />
                    </div>
                  </div>
                )}

                {/* Specific Fields for Image Banner Ads */}
                {editingAd.ad_type === "image" && (
                  <div className="space-y-3 p-3.5 bg-blue-50/40 border border-blue-200/60 rounded-lg">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-bold text-slate-700 uppercase">
                          Image or Animated GIF URL
                        </label>
                        <span className="text-[11px] text-blue-700 font-mono font-semibold">
                          Recommended: {getAdLayoutSpec(editingAd.placement).dimensions.desktop}
                        </span>
                      </div>
                      <input
                        type="url"
                        placeholder="https://example.com/campaign.gif"
                        value={editingAd.image_url}
                        onChange={(e) => setEditingAd({ ...editingAd, image_url: e.target.value })}
                        className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white"
                      />
                    </div>

                    <p className="text-xs text-slate-600">Animated .gif banners play automatically. Use an HTTPS GIF URL; the image fills its ad container.</p>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Image Alt Text
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Econet 5G Business Plan Banner"
                        value={editingAd.alt_text}
                        onChange={(e) => setEditingAd({ ...editingAd, alt_text: e.target.value })}
                        className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Priority and Active Toggle */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Priority Weight (1-100)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={editingAd.priority}
                      onChange={(e) => setEditingAd({ ...editingAd, priority: Number(e.target.value) })}
                      className="w-full text-sm border border-slate-300 rounded px-3 py-2 bg-white font-mono"
                    />
                  </div>

                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingAd.active}
                        onChange={(e) => setEditingAd({ ...editingAd, active: e.target.checked })}
                        className="w-4 h-4 text-amber-600 rounded border-slate-300"
                      />
                      <span className="text-xs font-bold text-slate-800 uppercase">
                        Campaign Active
                      </span>
                    </label>
                  </div>
                </div>

                {/* LIVE INTERACTIVE CREATIVE & LAYOUT PREVIEW */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900 uppercase">
                      <Eye size={15} className="text-amber-700" />
                      <span>Live Creative Preview (Real-Time Render)</span>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Viewport switcher */}
                      <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded border border-slate-200 text-xs">
                        <button
                          type="button"
                          onClick={() => setFormPreviewViewport("desktop")}
                          className={`flex items-center gap-1 px-2 py-1 rounded font-semibold transition-all ${
                            formPreviewViewport === "desktop"
                              ? "bg-white text-slate-900 shadow-2xs font-bold"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          <Monitor size={12} />
                          <span>Desktop</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormPreviewViewport("tablet")}
                          className={`flex items-center gap-1 px-2 py-1 rounded font-semibold transition-all ${
                            formPreviewViewport === "tablet"
                              ? "bg-white text-slate-900 shadow-2xs font-bold"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          <Tablet size={12} />
                          <span>Tablet</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormPreviewViewport("mobile")}
                          className={`flex items-center gap-1 px-2 py-1 rounded font-semibold transition-all ${
                            formPreviewViewport === "mobile"
                              ? "bg-white text-slate-900 shadow-2xs font-bold"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          <Smartphone size={12} />
                          <span>Mobile</span>
                        </button>
                      </div>

                      {/* Sample Content Overlay Toggle */}
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={formShowSampleContent}
                          onChange={(e) => setFormShowSampleContent(e.target.checked)}
                          className="w-3.5 h-3.5 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
                        />
                        <span>Sample Editorial Overlay</span>
                      </label>
                    </div>
                  </div>

                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 overflow-x-auto flex justify-center shadow-inner">
                    <div
                      style={{
                        width:
                          formPreviewViewport === "mobile"
                            ? "375px"
                            : formPreviewViewport === "tablet"
                            ? "720px"
                            : "100%",
                        maxWidth: "100%"
                      }}
                      className="transition-all duration-300"
                    >
                      <AdLayoutSchematic
                        placement={editingAd.placement}
                        adType={editingAd.ad_type || "image"}
                        name={editingAd.name}
                        headline={editingAd.headline || undefined}
                        sponsorName={editingAd.sponsor_name || undefined}
                        bodyText={editingAd.body_text || undefined}
                        imageUrl={editingAd.image_url || undefined}
                        ctaText={editingAd.cta_text || undefined}
                        destinationUrl={editingAd.destination_url}
                        htmlContent={editingAd.html_content || undefined}
                        altText={editingAd.alt_text || undefined}
                        interactive={true}
                        viewport={formPreviewViewport}
                        showSampleContent={formShowSampleContent}
                        viewMode="actual"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || uploadingCreative}
                    className="mag-admin-primary-btn"
                  >
                    {saving ? "Saving Campaign..." : editingAd.id ? "Update Campaign" : "Launch Campaign"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* AD LAYOUT MATRIX & DIMENSIONAL GUIDE MODAL */}
      <AdLayoutGuideModal
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
        selectedPlacement={selectedGuidePlacement}
        onSelectPlacement={(p) => {
          if (editingAd) {
            setEditingAd({ ...editingAd, placement: p });
          } else {
            setFilterPlacement(p);
          }
        }}
      />
    </section>
  );
}
